/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, bool, string, func,
} from 'prop-types';

// Material UI
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import CheckCircle from '@material-ui/icons/CheckCircle';
import Info from '@material-ui/icons/Info';
import Cancel from '@material-ui/icons/Cancel';

const styles = (theme) => {
    const { palette } = theme;
    return ({
        rootFormControl: {
            border: `solid 1px ${palette.grey8}`,
            width: '100%',
            backgroundColor: palette.common.white || '#fff',
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                '-webkit-box-shadow': '0 0 0 30px white inset !important',
            },
        },
        rootCheckCircleIcon: {
            color: palette.colorSuccess,
            padding: '0',
            maxWidth: '16px',
            position: 'absolute',
            top: '-5px',
        },
        rootErrorIcon: {
            color: palette.error.light,
        },
        rootInputLabel: {
            fontSize: '14px',
            fontWeight: '500',
            textTransform: 'uppercase',
            paddingLeft: '10px',
            paddingTop: '0',
            lineHeight: '14px',
            top: '0',
        },
        rootInputBase: {
            marginTop: '24px',
        },
        rootInput: {
            padding: '5px 10px',
        },
        asteriskInputLabel: {
            margin: '0px 0px 0px 0px',
            fontSize: '20px',
        },
        rootFormHelperText: {
            margin: '0px',
            padding: '3px 10px',
        },
        formHelperText: {
            fontSize: '12px',
            color: palette.white,
        },
        errorColor: {
            backgroundColor: 'red',
        },
        infoColor: {
            backgroundColor: palette.grey10,
        },
        infoArrow: {
            margin: '0px 0px',
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: `5px solid ${palette.grey10}`,
            position: 'absolute',
            right: '18px',
            top: '47px',
        },
    });
};

const CustomInputField = (props) => {
    const {
        fieldId,
        name,
        label,
        classes,
        placeholder,
        showInfo,
        isRequired,
        autoComplete,
        handleChange,
        handleBlur,
        handleOnFocus,
        handleOnKeyUp,
        val,
        flagError,
        errorMessage,
        infoHelpText,
        InputProps,
        disabled,
        toggleDisplayFieldInfo,
        inputFieldType,
    } = props;

    const endAdornment = (infoHelpText) ? (
        <InputAdornment position="end">
            <IconButton
                onClick={toggleDisplayFieldInfo}
            >
                {infoHelpText && (showInfo ? <Cancel /> : <Info />)}
            </IconButton>
        </InputAdornment>
    ) : '';

    return (
        <div>
            <FormControl
                classes={{
                    root: classes.rootFormControl,
                }}
                error={flagError}
            >
                <InputLabel
                    classes={{
                        root: classes.rootInputLabel,
                    }}
                    // shrink
                    htmlFor={name}
                >
                    <span>
                        <span>{label}</span>
                        {isRequired && <span className={classes.asteriskInputLabel}>*</span>}
                        {!flagError && isRequired && val && (
                            <CheckCircle classes={{ root: classes.rootCheckCircleIcon }} />
                        )}
                    </span>
                </InputLabel>
                <Input
                    classes={{
                        root: classes.rootInputBase,
                        input: classes.rootInput,
                    }}
                    disableUnderline
                    id={fieldId}
                    autoComplete={autoComplete}
                    required={isRequired}
                    name={name}
                    type={inputFieldType}
                    placeholder={placeholder}
                    key={`tf_input_${fieldId}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleOnFocus}
                    onKeyUp={handleOnKeyUp}
                    value={val}
                    error={flagError}
                    fullWidth
                    data-test={`checkout-text-field-div-wrapper-${name}`}
                    inputProps={InputProps}
                    disabled={disabled}
                    endAdornment={endAdornment}
                />
                {showInfo && (
                    <FormHelperText
                        id={`helper-text-${fieldId}`}
                        inputLabelProps={{
                            shrink: true,
                        }}
                        classes={{
                            root: `${classes.rootFormHelperText} ${classes.infoColor}`,
                        }}
                    >
                        <span className={classes.infoArrow} />
                        <span className={classes.formHelperText}>
                            {infoHelpText}
                        </span>
                    </FormHelperText>
                )}
                {flagError && (
                    <FormHelperText
                        id={`helper-text-${fieldId}`}
                        inputLabelProps={{
                            shrink: true,
                        }}
                        classes={{
                            root: `${classes.rootFormHelperText} ${classes.errorColor}`,
                        }}
                    >
                        <span className={classes.formHelperText}>
                            {errorMessage}
                        </span>
                    </FormHelperText>
                )}
            </FormControl>
        </div>
    );
};

CustomInputField.defaultProps = {
    label: '',
    fieldId: '',
    classes: {},
    showInfo: false,
    isRequired: false,
    name: '',
    val: '',
    infoHelpText: '',
    errorMessage: null,
    flagError: null,
    InputProps: {},
    placeholder: '',
    disabled: false,
    autoComplete: 'off',
    handleOnFocus: () => { },
    handleOnKeyUp: () => { },
    handleChange: () => {},
    handleBlur: () => {},
    toggleDisplayFieldInfo: () => {},
    inputFieldType: '',
};

CustomInputField.propTypes = {
    label: string,
    fieldId: string,
    classes: object,
    showInfo: bool,
    isRequired: bool,
    val: string,
    infoHelpText: string,
    name: string,
    handleOnFocus: func,
    handleOnKeyUp: func,
    errorMessage: string,
    flagError: bool,
    InputProps: object,
    placeholder: string,
    disabled: bool,
    autoComplete: string,
    handleChange: func,
    handleBlur: func,
    toggleDisplayFieldInfo: func,
    inputFieldType: string,
};

export default withStyles(styles)(CustomInputField);
